import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Popover } from '@headlessui/react'
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import { usePopper } from 'react-popper'
import {
  useDeleteBlacklineCommentMutation,
  useGetDocumentByUuidQuery,
  useGetUserProfileQuery,
  useUpdateBlacklineCommentMutation,
} from '../../redux/api-slice'
import TextareaAutosize from 'react-textarea-autosize'
import {
  DocumentBlacklineSegment,
  DocumentBlacklineSegmentComment,
} from '../../shared/interfaces/project/document/blackline/document-blackline.interface'
import { ProvisionUser } from '../../shared/interfaces/user/user.inteface'
import { skipToken } from '@reduxjs/toolkit/query'
import { useParams } from 'react-router-dom'
import {
  selectShowRedline,
  selectSelectedBlacklineSegment,
  selectEditRedlineComment,
  setEditRedlineComment,
} from '../../redux/viewer-slice'
import { useDispatch, useSelector } from 'react-redux'
import { selectCommentWorkflowFilterStateQueryParams } from '../../redux/workflow-slice'

interface DiffCommentComponentProps {
  documentBlacklineSegmentComment: DocumentBlacklineSegmentComment
  segment: DocumentBlacklineSegment
}

const DiffCommentComponent = ({
  documentBlacklineSegmentComment,
  segment,
}: DiffCommentComponentProps) => {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null
  )
  const [popperElement, setPanelRef] = useState<HTMLElement | null>(null)
  const { styles, attributes } = usePopper(referenceElement, popperElement)
  const [commentEditText, setCommentEditText] = useState(
    documentBlacklineSegmentComment.comment
  )
  const [deleteComment] = useDeleteBlacklineCommentMutation()
  const [updateComment] = useUpdateBlacklineCommentMutation()
  const { data: user } = useGetUserProfileQuery(undefined)
  const { documentId } = useParams()
  const { data: document } = useGetDocumentByUuidQuery(documentId ?? skipToken)
  const showRedline = useSelector(selectShowRedline)
  const selectedBlacklineSegment = useSelector(selectSelectedBlacklineSegment)
  const editRedlineComment = useSelector(selectEditRedlineComment)
  const dispatch = useDispatch()
  const revisionQueryParams = useSelector(
    selectCommentWorkflowFilterStateQueryParams
  )
  useEffect(() => {
    setCommentEditText(documentBlacklineSegmentComment.comment)
  }, [documentBlacklineSegmentComment.comment])

  const reset = useCallback(
    (e) => {
      e?.stopPropagation()
      dispatch(setEditRedlineComment(null))
      setCommentEditText(documentBlacklineSegmentComment.comment)
    },
    [documentBlacklineSegmentComment.comment, dispatch]
  )

  const onChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommentEditText(e.target.value)
  }, [])

  const onUpdateComment = useCallback(
    async (e) => {
      if (!documentBlacklineSegmentComment.id) {
        return
      }
      e?.stopPropagation()
      dispatch(setEditRedlineComment(null))
      await updateComment({
        id: documentBlacklineSegmentComment.id,
        comment: commentEditText,
        page: segment.page,
        document: document?.v1_document ?? 0,
        document_blackline_segment: segment.id,
        project: revisionQueryParams?.projectId ?? 0,
        documents: revisionQueryParams?.documentIds ?? [],
      })
    },
    [
      commentEditText,
      document?.v1_document,
      documentBlacklineSegmentComment.id,
      segment.id,
      segment.page,
      updateComment,
      dispatch,
      revisionQueryParams?.projectId,
      revisionQueryParams?.documentIds,
    ]
  )

  const onPressEnter = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        onUpdateComment(null)
      } else if (e.key === 'Escape') {
        reset(null)
      }
    },
    [onUpdateComment, reset]
  )

  const editTextArea = useMemo(() => {
    return (
      <>
        <TextareaAutosize
          data-cy={`blackline-segment-comment-edit-textarea-${documentBlacklineSegmentComment.id}`}
          className={
            'w-full flex-grow resize-none rounded-sm border-0 border-white  p-1 text-sm text-gray-800 placeholder-gray-400 focus:border focus:border-none focus:border-gray-300 focus:text-black focus:outline-none focus:ring-0'
          }
          autoFocus={
            showRedline ? selectedBlacklineSegment?.id === segment.id : false
          }
          placeholder="Edit comment"
          onChange={onChange}
          value={commentEditText}
          onKeyDown={onPressEnter}
        />
        <div className={'mr-2 flex justify-end gap-2 pt-1'}>
          <button
            type="button"
            className="self-start rounded border bg-white px-4 py-1 text-sm font-semibold shadow-sm hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={reset}
          >
            Cancel
          </button>
          <button
            data-cy={`blackline-segment-comment-save-button-${documentBlacklineSegmentComment.id}`}
            type="button"
            className={`self-start rounded border ${!documentBlacklineSegmentComment?.id ? 'border-gray-400 bg-gray-400' : 'border-indigo-600 bg-indigo-600'} px-4 py-1 text-sm font-semibold text-white shadow-sm ${!documentBlacklineSegmentComment?.id ? 'cursor-not-allowed' : 'hover:bg-indigo-500'} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600`}
            onClick={onUpdateComment}
            disabled={!documentBlacklineSegmentComment?.id}
          >
            Save
          </button>
        </div>
      </>
    )
  }, [
    documentBlacklineSegmentComment.id,
    showRedline,
    selectedBlacklineSegment?.id,
    segment.id,
    onChange,
    commentEditText,
    onPressEnter,
    reset,
    onUpdateComment,
  ])

  const renderComment = useCallback(
    (comment: DocumentBlacklineSegmentComment) => {
      const mentions = comment.mentions ?? []
      const elements: JSX.Element[] = []
      let index = 0
      if (editRedlineComment === comment.id) {
        return editTextArea
      }
      if (!mentions || mentions.length === 0) {
        return <span>{comment.comment}</span>
      }
      for (const mention of mentions) {
        if (
          mention.start_index === undefined ||
          mention.end_index === undefined
        ) {
          continue
        }
        elements.push(
          <span key={`mention_${mention.user_targeted_id}_${index}`}>
            {comment.comment?.slice(index, mention.start_index)}
          </span>
        )
        elements.push(
          <span
            key={`mention_${mention.user_targeted_id}_${index}_mention`}
            className="text-indigo-500"
          >
            {mention.user_targeted?.email}{' '}
          </span>
        )
        index = mention.end_index
      }
      elements.push(
        <span key={`mention_${mentions.length}_${index}`}>
          {comment.comment?.slice(index)}
        </span>
      )
      return elements
    },
    [editTextArea, editRedlineComment]
  )

  const onDeleteBlacklineSegmentComment = useCallback(() => {
    referenceElement?.click()
    deleteComment({
      id: documentBlacklineSegmentComment.id,
      segmentID: segment.id,
      page: segment.page,
      documentID: document?.v1_document ?? 0,
      projectID: revisionQueryParams?.projectId ?? 0,
      documents: revisionQueryParams?.documentIds ?? [],
    })
  }, [
    referenceElement,
    deleteComment,
    document?.v1_document,
    documentBlacklineSegmentComment.id,
    segment.id,
    segment.page,
    revisionQueryParams?.projectId,
    revisionQueryParams?.documentIds,
  ])

  const onEditBlacklineSegmentComment = useCallback(
    (e) => {
      e.stopPropagation()
      referenceElement?.click()
      dispatch(setEditRedlineComment(documentBlacklineSegmentComment.id))
    },
    [referenceElement, dispatch, documentBlacklineSegmentComment.id]
  )

  return (
    <div
      className="max-h-96 overflow-auto py-1"
      key={`blackline_segment_comment_${documentBlacklineSegmentComment?.id}`}
    >
      <div className="flex items-center justify-between text-sm">
        <div className={'space-x-1'}>
          <div className="inline flex-auto truncate text-sm font-semibold leading-6">
            {
              (documentBlacklineSegmentComment?.user_created as ProvisionUser)
                ?.first_name
            }{' '}
            {
              (documentBlacklineSegmentComment?.user_created as ProvisionUser)
                ?.last_name
            }
          </div>
          <div className="inline text-xs text-gray-400">
            {`${new Date(
              documentBlacklineSegmentComment?.date_created ||
                new Date().toISOString()
            ).toDateString()}`}
          </div>
        </div>
        {user?.id ===
        (documentBlacklineSegmentComment?.user_created as ProvisionUser)?.id ? (
          <Popover>
            <Popover.Button
              disabled={
                !documentBlacklineSegmentComment?.id ||
                documentBlacklineSegmentComment?.id === '-1'
              }
              className={
                'flex items-center justify-center rounded text-gray-600 hover:bg-gray-100'
              }
              tabIndex={-1}
              data-playwright={`diff_card_comment_options_${documentBlacklineSegmentComment?.id}`}
              ref={setReferenceElement}
            >
              <EllipsisVerticalIcon className="h-5 w-5" />
            </Popover.Button>
            <Popover.Panel
              ref={setPanelRef}
              style={styles.popper}
              {...attributes.popper}
              className="z-50 flex flex-col rounded border bg-white shadow"
            >
              <>
                <button
                  className="cursor-pointer px-4 py-2 text-left text-xs hover:bg-gray-100"
                  onClick={onEditBlacklineSegmentComment}
                >
                  Edit
                </button>
                <button
                  className="cursor-pointer px-4 py-2 text-left text-xs hover:bg-gray-100"
                  onClick={onDeleteBlacklineSegmentComment}
                >
                  Delete
                </button>
              </>
            </Popover.Panel>
          </Popover>
        ) : null}
      </div>
      <div
        className={`whitespace-pre-wrap`}
        key={`comment_${documentBlacklineSegmentComment?.id}`}
      >
        {renderComment(documentBlacklineSegmentComment)}
      </div>
    </div>
  )
}

export default DiffCommentComponent
